class UiBuildYourPlanNavigation extends HTMLElement {
  constructor() {
    super();
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.viewOrderSummaryButton = this.querySelector('[data-view-order-summary]');
    this.checkoutButton = this.querySelector('[data-checkout]');
  }

  updateNavigation(currentStep, totalSteps, isOrderSummaryCompleted) {
    const isOrderSummary = currentStep === totalSteps - 1;

    this.prevButton.classList.toggle('is-hidden', isOrderSummary);
    this.nextButton.classList.toggle('is-hidden', isOrderSummary);

    if (!isOrderSummary) {
      this.prevButton.disabled = currentStep === 0;
      const isLastStep = currentStep === totalSteps - 2;
      this.nextButton.textContent = isLastStep ? 'View my plan' : 'Next';
    }

    this.viewOrderSummaryButton.classList.toggle('is-hidden', !isOrderSummaryCompleted || isOrderSummary);
  }
}

customElements.define('ui-build-your-plan-navigation', UiBuildYourPlanNavigation);

class UiBuildYourPlanProgress extends HTMLElement {
  constructor() {
    super();
    this._steps = 0;
    this._currentStep = 0;
  }

  static get observedAttributes() {
    return ['steps', 'current-step'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'steps') {
      this._steps = parseInt(newValue, 10);
      this.render();
    } else if (name === 'current-step') {
      this._currentStep = parseInt(newValue, 10);
      this.updateActiveStep();
    }
  }

  render() {
    this.innerHTML = Array.from(
      { length: this._steps },
      (_, i) => `
      <div class="build-your-plan-progress__step${i === 0 ? ' is-active' : ''}">
        <p>${i + 1}</p>
      </div>
    `
    ).join('');
    this.updateActiveStep();
  }

  updateActiveStep() {
    const steps = this.querySelectorAll('.build-your-plan-progress__step');
    steps.forEach((step, index) => {
      step.classList.toggle('is-active', index === this._currentStep);
      step.classList.toggle('is-clickable', index <= this._currentStep);
    });
  }
}
customElements.define('ui-build-your-plan-progress', UiBuildYourPlanProgress);

class UiBuildYourPlanStep extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
  }

  show() {
    this.classList.remove('is-hidden');
    this.setAttribute('aria-hidden', 'false');
  }

  hide() {
    this.classList.add('is-hidden');
    this.setAttribute('aria-hidden', 'true');
  }

  getFirstOptionValue() {
    const firstOption = this.querySelector('.radio-group__input');
    return firstOption ? firstOption.value : null;
  }
}
customElements.define('ui-build-your-plan-step', UiBuildYourPlanStep);

class UiBuildYourPlanSummary extends HTMLElement {
  constructor() {
    super();
    this.main = this.querySelector('.build-your-plan-summary__main');
  }

  updateSummary(formData) {
    const summaryHTML = Object.entries(formData)
      .map(([key, { value, completed }]) => {
        const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        const displayValue = completed ? value.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : '—';
        return `
          <div class="summary-item">
            <p><strong>${readableKey}:</strong> ${displayValue}</p>
            ${completed ? `<button class="edit-button" data-step="${key}">Edit</button>` : ''}
          </div>
        `;
      })
      .join('');
    this.main.innerHTML = summaryHTML;

    this.querySelectorAll('.edit-button').forEach((button) => {
      button.addEventListener('click', (event) => {
        const stepName = event.target.dataset.step;
        this.dispatchEvent(
          new CustomEvent('build-your-plan:edit', {
            detail: { stepName },
            bubbles: true,
            composed: true,
          })
        );
      });
    });
  }
}
customElements.define('ui-build-your-plan-summary', UiBuildYourPlanSummary);

class UiBuildYourPlan extends HTMLElement {
  constructor() {
    super();
    this.steps = [];
    this.currentStep = 0;
    this.completedSteps = 0;
    this.formData = {};
    this.totalSteps = 0;
    this.isOrderSummaryCompleted = false;
    this.cookieName = null;
    this.expirationValue = 7; // Default to 7
    this.expirationUnit = 'days'; // Default to days
    this.isInitialized = false;
    this.variant_ID = null;
    this.variant_price = null;
    this.product = null;
    this.sellingPlan = null;
    this.error = false;
  }

  connectedCallback() {
    // this.classList.add('is-loading');
    this.steps = Array.from(this.querySelectorAll('ui-build-your-plan-step'));
    this.progressBar = this.querySelector('ui-build-your-plan-progress');
    this.form = this.querySelector('form');
    this.navigation = this.querySelector('ui-build-your-plan-navigation');
    this.summary = this.querySelector('ui-build-your-plan-summary');
    this.titleElement = this.querySelector('.build-your-plan__title');
    this.progressBar.setAttribute('steps', this.steps.length - 1); // Exclude summary step
    this.totalSteps = this.steps.length;

    this.checkoutButton = this.querySelector('button[name="checkout"]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // Set cookie name based on data-type attribute
    const dataType = this.getAttribute('data-type');
    this.cookieName = dataType === 'gift' ? 'quiz_gift' : 'quiz_subscription';

    // this.setDefaultFormData();
    this.initializeExpirationSettings();
    this.loadProgressFromCookie();
    // this.preselectOptions(); // Add this line

    this.render();
    this.setupEventListeners();
    // this.updateSummary();

    this.classList.remove('is-loading');
    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  initializeExpirationSettings() {
    const expirationValue = this.getAttribute('data-expiration-value');
    const expirationUnit = this.getAttribute('data-expiration-unit');

    if (expirationValue) {
      this.expirationValue = parseInt(expirationValue, 10);
    }

    if (expirationUnit && ['hours', 'days', 'weeks'].includes(expirationUnit)) {
      this.expirationUnit = expirationUnit;
    }
  }

  loadProgressFromCookie() {
    const cookieData = this.getCookie(this.cookieName);
    if (cookieData) {
      const { currentStep, formData, completedSteps, isOrderSummaryCompleted, variant, sellingPlan } =
        JSON.parse(cookieData);

      this.currentStep = currentStep;
      this.formData = formData;
      this.completedSteps = completedSteps;
      this.isOrderSummaryCompleted = isOrderSummaryCompleted;
      this.variant = variant || {};
      this.sellingPlan = sellingPlan || null;

      console.log('Loading progress from cookie:', JSON.parse(cookieData));

      // Preselect the options
      this.preselectOptions(variant);
    } else {
      console.log('No existing cookie found. Setting default form data.');
      this.setDefaultFormData();
    }
  }

  preselectOptions(variant) {
    this.variant_ID = variant.id;

    Object.entries(this.formData).forEach(([name, { value }]) => {
      const input = this.querySelector(`input[name="${name}"][value="${value}"]`);
      if (input) {
        input.checked = true;
      }
    });

    this.querySelector('.totals__total-value').textContent = `$${this.variant.price}`;
  }

  // saveProgressToCookie() {
  //   const data = JSON.stringify({
  //     currentStep: this.currentStep,
  //     formData: this.formData,
  //     completedSteps: this.completedSteps,
  //     isOrderSummaryCompleted: this.isOrderSummaryCompleted,
  //     variant: {
  //       id: this.variant_ID,
  //       price: this.variant_price,
  //     },
  //     sellingPlan: this.sellingPlan,
  //   });
  //   this.setCookie(this.cookieName, data, this.expirationValue, this.expirationUnit);
  // }

  // setCookie(name, value, expirationValue, expirationUnit) {
  //   const date = new Date();
  //   let milliseconds;

  //   switch (expirationUnit) {
  //     case 'hours':
  //       milliseconds = expirationValue * 60 * 60 * 1000;
  //       break;
  //     case 'weeks':
  //       milliseconds = expirationValue * 7 * 24 * 60 * 60 * 1000;
  //       break;
  //     case 'days':
  //     default:
  //       milliseconds = expirationValue * 24 * 60 * 60 * 1000;
  //   }

  //   date.setTime(date.getTime() + milliseconds);
  //   const expires = 'expires=' + date.toUTCString();
  //   document.cookie = name + '=' + value + ';' + expires + ';path=/';
  // }

  // getCookie(name) {
  //   const cookieName = name + '=';
  //   const decodedCookie = decodeURIComponent(document.cookie);
  //   const cookieArray = decodedCookie.split(';');
  //   for (let i = 0; i < cookieArray.length; i++) {
  //     let cookie = cookieArray[i];
  //     while (cookie.charAt(0) === ' ') {
  //       cookie = cookie.substring(1);
  //     }
  //     if (cookie.indexOf(cookieName) === 0) {
  //       return cookie.substring(cookieName.length, cookie.length);
  //     }
  //   }
  //   return null;
  // }

  // deleteCookie(name) {
  //   document.cookie = name + '=; Max-Age=-99999999; path=/';
  // }

  saveProgressToCookie() {
    try {
      const data = JSON.stringify({
        currentStep: this.currentStep,
        formData: this.formData,
        completedSteps: this.completedSteps,
        isOrderSummaryCompleted: this.isOrderSummaryCompleted,
        variant: {
          id: this.variant_ID,
          price: this.variant_price,
        },
        sellingPlan: this.sellingPlan,
      });
      this.setCookie(this.cookieName, data, this.expirationValue, this.expirationUnit);
    } catch (error) {
      console.error('Error saving cookie:', error);
    }
  }

  setCookie(name, value, expirationValue, expirationUnit) {
    if (!name || !value) return;

    const date = new Date();
    let milliseconds;

    switch (expirationUnit) {
      case 'hours':
        milliseconds = expirationValue * 60 * 60 * 1000;
        break;
      case 'weeks':
        milliseconds = expirationValue * 7 * 24 * 60 * 60 * 1000;
        break;
      case 'days':
      default:
        milliseconds = expirationValue * 24 * 60 * 60 * 1000;
    }

    date.setTime(date.getTime() + milliseconds);
    const expires = 'expires=' + date.toUTCString();
    document.cookie = `${name}=${value}; ${expires}; path=/`;
  }

  getCookie(name) {
    if (!name) return null;

    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');

    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim(); // Use trim() instead of while loop
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length);
      }
    }
    return null;
  }

  deleteCookie(name) {
    if (!name) return;
    document.cookie = `${name}=; Max-Age=-99999999; path=/`;
  }

  /*
   * Set default values for all form fields,
   * but mark them as not completed initially.
   */
  setDefaultFormData() {
    this.steps.forEach((step, index) => {
      if (step instanceof UiBuildYourPlanStep && index < this.steps.length - 1) {
        const name = step.querySelector('.radio-group__input')?.name;
        const defaultValue = step.getFirstOptionValue();
        if (name && defaultValue) {
          this.formData[name] = {
            value: defaultValue,
            completed: false,
          };
        }
      }
    });

    console.log('Initial choices:', this.formData);
  }

  // render() {
  //   this.updateProgressBar();
  //   this.renderCurrentStep();
  //   this.updateNavigation();
  //   this.updateTitle();
  //   this.updateSummary();

  //   console.log('Current Step:', this.currentStep + 1);
  //   console.log('Current choices:', this.formData);
  // }

  render() {
    try {
      if (this.progressBar) {
        this.updateProgressBar();
      }

      if (this.steps) {
        this.renderCurrentStep();
      }

      if (this.navigation && typeof this.navigation.updateNavigation === 'function') {
        this.updateNavigation();
      }

      if (this.titleElement) {
        this.updateTitle();
      }

      if (this.summary && typeof this.summary.updateSummary === 'function') {
        this.updateSummary();
      }

      console.log('Current Step:', this.currentStep + 1);
      console.log('Current choices:', this.formData);
    } catch (error) {
      console.error('Error in render:', error);
    }
  }

  setupEventListeners() {
    this.navigation.addEventListener('click', this.handleNavigation.bind(this));
    this.form.addEventListener('change', this.handleInput.bind(this));
    this.form.addEventListener('click', this.handleRadioClick.bind(this));
    this.progressBar.addEventListener('click', this.handleProgressBarClick.bind(this));
    this.summary.addEventListener('build-your-plan:edit', this.handleEdit.bind(this));
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  removeEventListeners() {
    this.navigation.removeEventListener('click', this.handleNavigation.bind(this));
    this.form.removeEventListener('change', this.handleInput.bind(this));
    this.form.removeEventListener('click', this.handleRadioClick.bind(this));
    this.progressBar.removeEventListener('click', this.handleProgressBarClick.bind(this));
    this.summary.removeEventListener('build-your-plan:edit', this.handleEdit.bind(this));
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  // updateNavigation() {
  //   this.navigation.updateNavigation(this.currentStep, this.totalSteps, this.isOrderSummaryCompleted);
  // }

  updateNavigation() {
    if (this.navigation && typeof this.navigation.updateNavigation === 'function') {
      this.navigation.updateNavigation(this.currentStep, this.totalSteps, this.isOrderSummaryCompleted);
    }
  }

  async handleNavigation(event) {
    if (event.target.hasAttribute('data-next')) {
      // Check if the next step would be the order summary (second-to-last step)
      if (this.currentStep === this.totalSteps - 2) {
        try {
          await this.findProduct();
          if (!this.variant_ID) {
            throw new Error('No matching product variant found');
          }
          // If product is found, proceed to the next step
          this.nextStep();
        } catch (error) {
          console.error('Error finding product:', error);
          // Handle the error (e.g., show an error message to the user)
        }
      } else {
        // For all other steps, just move to the next step
        this.nextStep();
      }
    } else if (event.target.hasAttribute('data-prev')) {
      this.prevStep();
    } else if (event.target.hasAttribute('data-view-order-summary')) {
      try {
        await this.findProduct();
        if (!this.variant_ID) {
          throw new Error('No matching product variant found');
        }
        this.goToStep(this.totalSteps - 1);
      } catch (error) {
        console.error('Error finding product:', error);
        // Handle the error (e.g., show an error message to the user)
      }
    }
  }

  handleInput(event) {
    const { name, value } = event.target;
    if (this.formData[name].value !== value) {
      this.formData[name] = {
        value: value,
        completed: true,
      };
      this.updateSummary();
      this.saveProgressToCookie();

      // Automatically advance to next step after a short delay
      setTimeout(() => {
        if (this.currentStep < this.totalSteps - 1) {
          if (this.currentStep === this.totalSteps - 2) {
            this.findProduct()
              .then(() => {
                if (this.variant_ID) {
                  this.nextStep();
                }
              })
              .catch((error) => {
                console.error('Error finding product:', error);
              });
          } else {
            this.nextStep();
          }
        }
      }, 300);
    }
  }

  handleRadioClick(event) {
    // Only handle clicks on radio inputs
    if (event.target.type !== 'radio') return;

    const { name, value, checked } = event.target;

    // If the radio is already checked (meaning it was clicked again)
    if (checked && this.formData[name].value === value) {
      setTimeout(() => {
        if (this.currentStep < this.totalSteps - 1) {
          if (this.currentStep === this.totalSteps - 2) {
            this.findProduct()
              .then(() => {
                if (this.variant_ID) {
                  this.nextStep();
                }
              })
              .catch((error) => {
                console.error('Error finding product:', error);
              });
          } else {
            this.nextStep();
          }
        }
      }, 300);
    }
  }

  handleProgressBarClick(event) {
    const clickedStep = event.target.closest('.build-your-plan-progress__step');
    if (clickedStep) {
      const stepIndex = Array.from(clickedStep.parentNode.children).indexOf(clickedStep);

      if ((stepIndex < this.currentStep || stepIndex <= this.completedSteps) && stepIndex !== this.currentStep) {
        this.goToStep(stepIndex);
      }
    }
  }

  updateNavigationVisibility() {}

  handleEdit(event) {
    const { stepName } = event.detail;
    const stepIndex = this.steps.findIndex((step) => step.querySelector('.radio-group__input')?.name === stepName);
    if (stepIndex !== -1) {
      this.goToStep(stepIndex);
      this.updateNavigationVisibility();
      this.selectCurrentOption(stepIndex);
      this.saveProgressToCookie();
    }
  }

  selectCurrentOption(stepIndex) {
    const step = this.steps[stepIndex];
    const inputName = step.querySelector('.radio-group__input')?.name;
    if (inputName && this.formData[inputName]) {
      const selectedValue = this.formData[inputName].value;
      const radioInput = step.querySelector(`.radio-group__input[value="${selectedValue}"]`);
      if (radioInput) {
        radioInput.checked = true;
      }
    }
  }

  goToStep(stepIndex) {
    if (stepIndex >= 0 && stepIndex < this.totalSteps) {
      this.currentStep = stepIndex;
      this.render();
      this.updateNavigationVisibility();
      this.selectCurrentOption(stepIndex);
      this.saveProgressToCookie();
    }
  }

  /*
   * Past steps are alwasy clickable
   * The current step is never clickable
   * Future steps become clickable once they've been completed
   *
   *
   */
  updateProgressBar() {
    if (this.progressBar && this.currentStep < this.steps.length - 1) {
      this.progressBar.style.display = 'flex';
      this.progressBar.setAttribute('current-step', this.currentStep);

      const progress = (this.currentStep / (this.steps.length - 2)) * 100;
      this.progressBar.setAttribute('progress', progress);
      this.progressBar.style.setProperty('--progress', `${progress}%`);

      // Update the active state for all steps
      const stepElements = this.progressBar.querySelectorAll('.build-your-plan-progress__step');
      stepElements.forEach((step, index) => {
        step.classList.toggle('is-active', index === this.currentStep);
        // step.classList.toggle('is-clickable', index <= this.completedSteps);
        // step.classList.toggle('is-clickable', index < this.currentStep || index <= this.completedSteps);
        step.classList.toggle(
          'is-clickable',
          (index < this.currentStep || index <= this.completedSteps) && index !== this.currentStep
        );
      });
    } else {
      this.progressBar.style.display = 'none';
    }
  }

  // renderCurrentStep() {
  //   this.steps.forEach((step, index) => {
  //     if (index === this.currentStep) {
  //       step.show();
  //     } else {
  //       step.hide();
  //     }
  //   });
  // }

  renderCurrentStep() {
    this.steps.forEach((step, index) => {
      if (step && typeof step.show === 'function' && typeof step.hide === 'function') {
        if (index === this.currentStep) {
          step.show();
        } else {
          step.hide();
        }
      }
    });
  }

  updateTitle() {
    const currentStepElement = this.steps[this.currentStep];
    const stepTitle = currentStepElement.getAttribute('data-step-title');

    if (this.currentStep < this.steps.length - 1) {
      this.titleElement.textContent = `Step ${this.currentStep + 1}: ${stepTitle}`;
    } else {
      this.titleElement.textContent = stepTitle;
    }
  }

  // updateSummary() {
  //   if (this.summary) {
  //     this.summary.updateSummary(this.formData, this.completedSteps);
  //   }
  // }

  updateSummary() {
    if (this.summary && typeof this.summary.updateSummary === 'function') {
      this.summary.updateSummary(this.formData, this.completedSteps);
    }
  }

  updateFormData() {
    const currentInput = this.steps[this.currentStep].querySelector('.radio-group__input:checked');
    if (currentInput) {
      this.formData[currentInput.name] = {
        value: currentInput.value,
        completed: true,
      };
    } else {
      const inputName = this.steps[this.currentStep].querySelector('.radio-group__input')?.name;
      if (inputName && this.formData[inputName]) {
        this.formData[inputName].completed = true;
      }
    }
  }

  nextStep() {
    if (this.currentStep < this.totalSteps - 1) {
      this.completedSteps = Math.max(this.completedSteps, this.currentStep + 1);
      this.updateFormData();
      this.currentStep++;

      if (this.currentStep === this.totalSteps - 1) {
        this.isOrderSummaryCompleted = true;
      }

      this.render();
      this.updateNavigationVisibility();
      this.saveProgressToCookie();
    } else {
      // this.handleCheckout();
    }
  }

  prevStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.render();
      this.updateNavigationVisibility();
      this.saveProgressToCookie();
    }
  }

  async handleSubmit(event) {
    if (event) event.preventDefault();
    console.log('Submitting form with final choices:', this.formData);

    this.checkoutButton.setAttribute('disabled', 'true');
    this.checkoutButton.classList.add('loading');

    if (this.querySelector('.loading__spinner')) {
      this.querySelector('.loading__spinner').classList.remove('hidden');
    }

    try {
      if (!this.variant_ID) {
        await this.findProduct();
      }
      if (!this.variant_ID) {
        throw new Error('No matching product variant found');
      }
      // await this.addItemToCart();

      //prepare form data
      const formData = new FormData();
      formData.append('id', this.variant_ID);
      formData.append('quantity', 1);

      if (this.sellingPlan) {
        formData.append('selling_plan', this.sellingPlan.id);
      }

      // Add sections data if cart exists
      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      // Prepare fetch config
      // const config = {
      //   method: 'POST',
      //   headers: {
      //     'X-Requested-With': 'XMLHttpRequest',
      //   },
      //   body: formData,
      // };

      //   console.log('Form submitted successfully');

      //   // After successful checkout, clear the cookie and reset the component
      //   this.deleteCookie(this.cookieName);
      //   this.resetComponent();
      // } catch (error) {
      //   console.error(error);
      //   // Handle the error (e.g., show an error message to the user)
      // }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      console.log('config:', config);

      const formDataObj = Object.fromEntries(formData.entries());
      console.log('formDataObj', formDataObj);

      // Add to cart
      const response = await fetch(`${routes.cart_add_url}`, config);
      const responseJson = await response.json();

      console.log('response:', response);
      console.log('Response JSON:', responseJson);

      if (responseJson.status) {
        // Handle error response
        const errorMessage = responseJson.description || responseJson.message;
        console.error('Error adding to cart:', errorMessage);

        publish(PUB_SUB_EVENTS.cartError, {
          source: 'ui-build-your-plan',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });

        throw new Error(errorMessage);
      }

      // Success handling
      console.log('Successfully added to cart:', responseJson);

      // Success handling
      if (!this.cart) {
        // If no cart drawer/notification exists, redirect to cart page
        window.location = window.routes.cart_url;
        return;
      }

      // Update cart contents
      this.cart.renderContents(responseJson);

      console.log('renderContents response:', responseJson);

      // Clear cookie and reset form

      console.log('handleSubmit() this.cookieName:', this.cookieName);
      this.deleteCookie(this.cookieName);
      this.resetComponent();

      console.log('Form submitted successfully');
    } catch (error) {
      console.error('Error in form submission:', error);
      // You might want to show this error to the user
      this.error = true;
    } finally {
      // Reset button state
      this.checkoutButton.classList.remove('loading');
      if (this.cart && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }
      if (!this.error) {
        this.checkoutButton.removeAttribute('aria-disabled');
      }
      if (this.querySelector('.loading__spinner')) {
        this.querySelector('.loading__spinner').classList.add('hidden');
      }
    }
  }

  // async addItemToCart() {
  //   const formData = {
  //     items: [
  //       {
  //         id: this.variant_ID,
  //         quantity: 1,
  //         // selling_plan: '4260495460',
  //         // properties: {
  //         //   '': '*** This product will only be charged one time. This is NOT A SUBSCRIPTION.',
  //         //   // charge_interval_frequency: charge_frequency,
  //         //   // charge_interval_unit_type: unit_type,
  //         // },
  //         selling_plan: this.sellingPlan?.id,
  //         // properties: {
  //         //   shipping_interval_frequency: shippingFrequency.toString(),
  //         //   shipping_interval_unit_type: 'Weeks',
  //         //   charge_interval_frequency: chargeFrequency,
  //         //   charge_interval_unit_type: 'Weeks',
  //         // },
  //       },
  //     ],
  //   };

  //   try {
  //     // Clear the cart
  //     // await fetch('/cart/clear.js', { method: 'POST' });

  //     // Add new item to cart
  //     const response = await fetch('/cart/add.js', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(formData),
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Failed to add item to cart: ${response.statusText}`);
  //     }

  //     const data = await response.json();
  //     console.log('Item added to cart:', data);

  //     // Call ReCharge function to process cart
  //     // this.reChargeProcessCart();
  //   } catch (error) {
  //     console.error('Error adding item to cart:', error);

  //     // Handle the error (e.g., show an error message to the user)
  //   }
  // }

  // resetComponent() {
  //   this.currentStep = 0;
  //   this.completedSteps = 0;
  //   this.formData = {};
  //   this.isOrderSummaryCompleted = false;
  //   this.variant_ID = null;
  //   this.variant_price = null;
  //   this.sellingPlan = null;
  //   this.error = false;

  //   // Reset form
  //   this.setDefaultFormData();

  //   // Reset all radio buttons to their default state
  //   this.steps.forEach((step, index) => {
  //     if (step instanceof UiBuildYourPlanStep && index < this.steps.length - 1) {
  //       const inputs = step.querySelectorAll('.radio-group__input');
  //       inputs.forEach((input, inputIndex) => {
  //         input.checked = inputIndex === 0; // Check first option only
  //       });
  //     }
  //   });

  //   // Reset price display if it exists
  //   const totalValueElement = this.querySelector('.totals__total-value');
  //   if (totalValueElement) {
  //     totalValueElement.textContent = '$0';
  //   }

  //   //rerender the component
  //   this.render();
  // }

  resetComponent() {
    // Reset state variables
    this.currentStep = 0;
    this.completedSteps = 0;
    this.formData = {};
    this.isOrderSummaryCompleted = false;
    this.variant_ID = null;
    this.variant_price = null;
    this.sellingPlan = null;
    this.error = false;

    // Reset checkout button
    if (this.checkoutButton) {
      this.checkoutButton.removeAttribute('disabled');
      this.checkoutButton.removeAttribute('aria-disabled');
      this.checkoutButton.classList.remove('loading');

      // Reset loading spinner if it exists
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }

    // Reset all radio buttons to their default state
    this.steps.forEach((step, index) => {
      if (step instanceof UiBuildYourPlanStep && index < this.steps.length - 1) {
        const inputs = step.querySelectorAll('.radio-group__input');
        inputs.forEach((input, inputIndex) => {
          input.checked = inputIndex === 0; // Check first option only
        });
      }
    });

    // Reset price display
    const totalValueElement = this.querySelector('.totals__total-value');
    if (totalValueElement) {
      totalValueElement.textContent = '$0';
    }

    // Reset summary section
    if (this.summary) {
      this.summary.updateSummary(this.formData);
    }

    // Reset navigation
    if (this.navigation) {
      this.navigation.updateNavigation(0, this.totalSteps, false);
    }

    // Reset progress bar
    if (this.progressBar) {
      this.progressBar.setAttribute('current-step', '0');
      const steps = this.progressBar.querySelectorAll('.build-your-plan-progress__step');
      steps.forEach((step, index) => {
        step.classList.toggle('is-active', index === 0);
        step.classList.toggle('is-clickable', false);
      });
    }

    // Set default form data
    this.setDefaultFormData();

    // Remove any error messages or states if they exist
    const errorMessages = this.querySelectorAll('.error-message');
    errorMessages.forEach((error) => error.remove());

    // Reset any disabled or modified classes on the component
    this.classList.remove('is-loading', 'has-error', 'is-submitted');

    // Re-render the component
    this.render();
  }

  async findProduct() {
    try {
      let allProducts = [];
      let page = 1;
      let hasMoreProducts = true;

      const toKebabCase = (str) => {
        return str
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
      };

      // Convert kebab-case to lowercase with spaces
      const fromKebabCase = (str) => {
        return str.replace(/-/g, ' ').toLowerCase();
      };

      while (hasMoreProducts) {
        const response = await fetch(`/products.json?limit=250&page=${page}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        allProducts = allProducts.concat(data.products);
        if (data.products.length < 250) {
          hasMoreProducts = false;
        } else {
          page++;
        }

        const product = allProducts.find(({ product_type, tags, title }) => {
          const lcTags = tags.map((e) => e.toLowerCase());
          console.log('find', product_type, tags, title);
          // console.log('lcTags', lcTags);
          // console.log('fromKebabCase(this.formData.delivery)', this.formData.delivery);
          console.log('this.formData', this.formData);
          console.log('toKebabCase(product_type', toKebabCase(product_type));
          console.log('this.formData.delivery_frequency', fromKebabCase(this.formData.delivery_frequency.value));
          return (
            toKebabCase(product_type) === this.formData.payment_options.value &&
            title.toLowerCase().includes('all stars') &&
            lcTags.includes(fromKebabCase(this.formData.delivery_frequency.value))
          );
        });

        if (!product) {
          console.log('No matching product found');
          throw new Error('No matching product found');
        }

        console.log('*** this.formData.roast_type.value', toKebabCase(this.formData.roast_type.value));

        // const variant = product.variants.find(
        //   ({ option1, option2 }) =>
        //     option1.toLowerCase() === fromKebabCase(this.formData.roast_type.value) &&
        //     option2.toLowerCase() === fromKebabCase(this.formData.grind_type.value)
        // );

        const variant = product.variants.find(({ option1, option2 }) => {
          const normalizeRoastType = (str) => {
            return str
              .toLowerCase()
              .replace(/[\/-]/g, '-') // Convert both forward slashes and hyphens to hyphens
              .replace(/\s+/g, '-');
          };

          const normalizedOption1 = normalizeRoastType(option1);
          const normalizedFormValue = normalizeRoastType(this.formData.roast_type.value);

          console.log('Variant matching:', {
            originalOption1: option1,
            normalizedOption1: normalizedOption1,
            formValue: this.formData.roast_type.value,
            normalizedFormValue: normalizedFormValue,
          });

          return (
            normalizedOption1 === normalizedFormValue &&
            option2.toLowerCase() === fromKebabCase(this.formData.grind_type.value)
          );
        });

        if (!variant) {
          console.log('No matching variant found');

          console.log('product.variants', product.variants);
          throw new Error('No matching variant found');
        }

        this.variant_ID = variant.id;
        this.variant_price = variant.price;

        await this.fetchProductDetails(product.handle);
        const filteredProduct = { ...product, variants: [variant] };

        this.saveProgressToCookie();

        console.log('Product found:', filteredProduct);
        console.log('Variant ID:', this.variant_ID);

        console.log('filteredProduct', filteredProduct);

        this.querySelector('.totals__total-value').textContent = `$${this.variant_price}`;
        return filteredProduct;
      }

      console.log('No matching product found');
      return null;
    } catch (error) {
      console.error('Error in findProduct:', error);
      throw error;
    }
  }

  async fetchProductDetails(handle) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const productData = await response.json();
      console.log('Product data:', productData);

      // // Find the selling plan that matches our criteria
      // const sellingPlanGroup = productData.selling_plan_groups.find(
      //   (group) =>
      //     group.name.toLowerCase().includes('all stars') &&
      //     group.name.toLowerCase().includes(this.formData.delivery_frequency.value.replaceAll('-', ' ')) &&
      //     group.name.toLowerCase().includes(this.formData.payment_options.value.replaceAll('-', ' '))
      // );

      // console.log('Selling plan group:', sellingPlanGroup);

      // const deliveryFrequency = this.formData.delivery_frequency.value.match(/\d+/)[0]; // Extract number from "every-2-weeks" -> "2"
      // const paymentOption = this.formData.payment_options.value.match(/\d+/)?.[0] || ''; // Extract number from "pay-for-6-shipments" -> "6"

      // console.log('sellingPlanGroup', productData.selling_plan_groups);

      // Find the selling plan group that matches our criteria
      // const sellingPlanGroup = productData.selling_plan_groups.find((group) => {
      //   const groupName = group.name.toLowerCase();

      //   // Debug logging
      //   console.log('Checking group:', {
      //     groupName,
      //     deliveryWeeks: `${deliveryFrequency} weeks`,
      //     paymentOption: paymentOption,
      //   });

      //   return (
      //     // Check if name includes delivery frequency (e.g., "every 2 weeks")
      //     groupName.includes(`every ${deliveryFrequency} weeks`) &&
      //     // For prepaid plans, check if it matches the number of shipments
      //     (this.formData.payment_options.value.includes('prepaid')
      //       ? groupName.includes(`charge every ${deliveryFrequency * paymentOption} weeks`)
      //       : // For regular subscriptions, just match the delivery frequency
      //         groupName.includes('charge every'))
      //   );
      // });

      const sellingPlanGroup = productData.selling_plan_groups[0];

      console.log('Found selling plan groups:', sellingPlanGroup);

      // console.log('Selling plan:', sellingPlanGroup.selling_plans[0]);

      if (!sellingPlanGroup || !sellingPlanGroup.selling_plans.length) {
        throw new Error('No matching selling plan found');
      }

      const sellingPlan = sellingPlanGroup.selling_plans[0];

      console.log('Selling plan:', sellingPlan);

      this.sellingPlan = sellingPlanGroup.selling_plans[0];

      this.product = {};
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  }
}

customElements.define('ui-build-your-plan', UiBuildYourPlan);
